import { useState, useEffect } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import { ArrowLeft } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { HeroSection } from "@/components/HeroSection";
import { TickerBar } from "@/components/TickerBar";
import { SeasonCard } from "@/components/SeasonCard";
import { VideoCard } from "@/components/VideoCard";
import { ProductsSection } from "@/components/ProductsSection";
import { MusicSection } from "@/components/MusicSection";
import { Footer } from "@/components/Footer";
import { FunnyLoader, useEmptyPhrases } from "@/components/FunnyLoader";
import { MetaTags } from "@/components/MetaTags";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to determine contrast color for text
const getContrastColor = (hexColor) => {
  if (!hexColor) return "#000000";
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? "#000000" : "#FFFFFF";
};

// Helper to get optimal items per row for balanced layout
// 6 or less: single row
// 7: 4+3, 8: 4+4, 9: 5+4, 10: 5+5, etc.
const getItemsPerRow = (itemCount) => {
  if (itemCount <= 6) return itemCount;
  return Math.ceil(itemCount / 2);
};

// ShowCard component for displaying shows
const ShowCard = ({ show, onClick, index }) => {
  return (
    <div
      onClick={() => onClick(show.slug || show.id)}
      className="cursor-pointer card-jump hand-drawn-subtle animate-fade-in opacity-0 group bg-black"
      style={{ animationDelay: `${index * 0.1}s`, animationFillMode: "forwards", borderWidth: '4px', borderColor: '#fff', borderStyle: 'solid' }}
      data-testid={`show-card-${show.id}`}
    >
      <div className="aspect-[2/3] relative overflow-hidden bg-black">
        {show.thumbnail ? (
          <img
            src={show.thumbnail}
            alt={show.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#E91E8C] to-[#FFE500]">
            <span className="text-4xl">🎬</span>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4 text-center">
          <h3 className="text-white font-bold text-lg md:text-xl line-clamp-2 drop-shadow-lg">
            {show.title}
          </h3>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-[#E91E8C] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
      </div>
    </div>
  );
};

export default function HomePage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [shows, setShows] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [videos, setVideos] = useState([]);
  const [selectedShow, setSelectedShow] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [banners, setBanners] = useState([]);
  const [shopProducts, setShopProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { getRandomPhrase } = useEmptyPhrases();
  const [emptyPhrase, setEmptyPhrase] = useState("");

  useEffect(() => {
    fetchData();
    setEmptyPhrase(getRandomPhrase());
  }, []);

  // Handle URL parameters - use location.search to detect all URL changes
  useEffect(() => {
    const showParam = searchParams.get('show');
    const seasonParam = searchParams.get('season');
    
    if (seasonParam) {
      // If season param exists, we need to find its show first
      fetchSeasonAndShow(seasonParam);
    } else if (showParam) {
      setSelectedShow(showParam);
      setSelectedSeason(null);
      setVideos([]);
    } else {
      // No params, reset to home view
      setSelectedShow(null);
      setSelectedSeason(null);
      setSeasons([]);
      setVideos([]);
    }
  }, [location.search]);

  useEffect(() => {
    if (selectedShow) {
      fetchSeasons(selectedShow);
    }
  }, [selectedShow]);

  useEffect(() => {
    if (selectedSeason) {
      fetchVideos(selectedSeason);
    }
  }, [selectedSeason]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [showsRes, bannersRes, shopRes] = await Promise.all([
        axios.get(`${API}/shows`),
        axios.get(`${API}/banners?active_only=true`).catch(() => ({ data: [] })),
        axios.get(`${API}/shop-products`).catch(() => ({ data: [] }))
      ]);
      setShows(showsRes.data);
      setBanners(bannersRes.data);
      setShopProducts(shopRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSeasonAndShow = async (seasonId) => {
    try {
      const seasonRes = await axios.get(`${API}/seasons/${seasonId}`);
      const season = seasonRes.data;
      if (season.show_id) {
        setSelectedShow(season.show_id);
      }
      setSelectedSeason(seasonId);
    } catch (error) {
      console.error("Error fetching season:", error);
    }
  };

  const fetchSeasons = async (showIdOrSlug) => {
    try {
      // First get the show to obtain its actual ID
      const showRes = await axios.get(`${API}/shows/${showIdOrSlug}`);
      const show = showRes.data;
      const res = await axios.get(`${API}/seasons?show_id=${show.id}`);
      setSeasons(res.data);
    } catch (error) {
      console.error("Error fetching seasons:", error);
    }
  };

  const fetchVideos = async (seasonIdOrSlug) => {
    try {
      // First get the season to obtain its actual ID
      const seasonRes = await axios.get(`${API}/seasons/${seasonIdOrSlug}`);
      const season = seasonRes.data;
      const res = await axios.get(`${API}/videos?season_id=${season.id}`);
      setVideos(res.data);
    } catch (error) {
      console.error("Error fetching videos:", error);
    }
  };

  const handleShowSelect = async (showIdOrSlug) => {
    setSelectedShow(showIdOrSlug);
    setSelectedSeason(null);
    setSeasons([]);
    setVideos([]);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Check if show has only 1 season - if so, skip to episodes
    try {
      // First get the show to obtain its actual ID
      const showRes = await axios.get(`${API}/shows/${showIdOrSlug}`);
      const show = showRes.data;
      const res = await axios.get(`${API}/seasons?show_id=${show.id}`);
      const showSeasons = res.data;
      setSeasons(showSeasons);
      
      if (showSeasons.length === 1) {
        // Auto-select the only season (use slug if available)
        setSelectedSeason(showSeasons[0].slug || showSeasons[0].id);
      }
    } catch (error) {
      console.error("Error fetching seasons:", error);
    }
  };

  const handleSeasonSelect = (seasonIdOrSlug) => {
    setSelectedSeason(seasonIdOrSlug);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToShows = () => {
    setSelectedShow(null);
    setSelectedSeason(null);
    setSeasons([]);
    setVideos([]);
  };

  const handleBackToSeasons = () => {
    // If show has only 1 season, go back to shows instead
    if (seasons.length === 1) {
      handleBackToShows();
    } else {
      setSelectedSeason(null);
      setVideos([]);
    }
  };

  const currentShow = shows.find(s => s.id === selectedShow || s.slug === selectedShow);
  const currentSeason = seasons.find(s => s.id === selectedSeason || s.slug === selectedSeason);

  // Show carousel and ticker only on main homepage (no show or season selected)
  const showHeroAndTicker = !selectedShow && !selectedSeason;

  // Generate meta tags based on current view
  const getMetaTags = () => {
    if (selectedSeason && currentSeason) {
      // Viewing episodes of a season
      return {
        title: `${currentSeason.title} - Episodios`,
        description: `Mira todos los episodios de ${currentSeason.title}`,
        image: currentSeason.thumbnail,
        url: `/?season=${selectedSeason}`
      };
    } else if (selectedShow && currentShow) {
      // Viewing seasons of a show
      return {
        title: `${currentShow.title} - Temporadas`,
        description: currentShow.description || `Mira todas las temporadas de ${currentShow.title}`,
        image: currentShow.thumbnail,
        url: `/?show=${currentShow.slug || selectedShow}`
      };
    }
    // Homepage
    return {
      title: null,
      description: 'Contenido original de Darkar Company Studios - Shows, Videos, Música y más',
      image: null,
      url: '/'
    };
  };

  const metaTags = getMetaTags();

  return (
    <div className="min-h-screen flex flex-col" data-testid="home-page">
      <MetaTags {...metaTags} />
      <Navbar />
      
      {/* Hero Banner Carousel - Only on homepage */}
      {showHeroAndTicker && (
        <HeroSection 
          banners={banners}
          loading={loading}
        />
      )}

      {/* Scrolling Ticker - Only on homepage */}
      {showHeroAndTicker && <TickerBar />}

      {/* Main Content */}
      <main className={`px-4 md:px-8 lg:px-12 pb-20 ${showHeroAndTicker ? 'mt-8' : 'pt-32'}`}>
        <div className="max-w-7xl mx-auto">
        
        {/* Level 3: Videos View (when season is selected) */}
        {selectedSeason ? (
          <section className="animate-fade-in" data-testid="videos-detail">
            {/* Header Row - Back Button + Episodes Badge */}
            <div className="flex items-center gap-4 mb-10">
              <button
                onClick={handleBackToSeasons}
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black font-black uppercase tracking-wide hand-drawn-subtle hover:bg-[#FFE500] transition-colors flex-shrink-0"
                style={{ borderWidth: '3px', borderColor: '#000', borderStyle: 'solid' }}
                data-testid="back-to-seasons-btn"
              >
                <ArrowLeft size={20} />
                <span>Atrás</span>
              </button>

              <h2 
                className="flex-1 text-center px-8 py-3 text-xl md:text-2xl font-black uppercase tracking-wide hand-drawn-subtle"
                style={{ 
                  backgroundColor: currentSeason?.color || '#FFE500',
                  color: getContrastColor(currentSeason?.color || '#FFE500'),
                  borderWidth: '3px', 
                  borderColor: getContrastColor(currentSeason?.color || '#FFE500'), 
                  borderStyle: 'solid' 
                }}
              >
                {currentSeason?.title || 'Episodios'}
              </h2>
            </div>

            {/* Videos Grid */}
            {videos.length === 0 ? (
              <p className="text-gray-400 text-lg">No hay episodios en esta temporada.</p>
            ) : (
              <div className="video-grid" data-testid="video-grid">
                {videos.map((video, index) => (
                  <VideoCard 
                    key={video.id} 
                    video={video}
                    index={index}
                    seasonTitle={currentSeason?.title}
                    seasonColor={currentSeason?.color}
                  />
                ))}
              </div>
            )}
          </section>

        /* Level 2: Seasons View (when show is selected) */
        ) : selectedShow ? (
          <section className="animate-fade-in" data-testid="seasons-detail">
            {/* Header Row - Back Button + Seasons Badge */}
            <div className="flex items-center gap-4 mb-10">
              <button
                onClick={handleBackToShows}
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black font-black uppercase tracking-wide hand-drawn-subtle hover:bg-[#FFE500] transition-colors flex-shrink-0"
                style={{ borderWidth: '3px', borderColor: '#000', borderStyle: 'solid' }}
                data-testid="back-to-shows-btn"
              >
                <ArrowLeft size={20} />
                <span>Atrás</span>
              </button>

              <h2 
                className="flex-1 text-center px-8 py-3 bg-black text-white text-xl md:text-2xl font-black uppercase tracking-wide hand-drawn-subtle"
                style={{ borderWidth: '3px', borderColor: '#fff', borderStyle: 'solid' }}
              >
                Temporadas
              </h2>
            </div>

            {/* Seasons Grid - Fixed height container with centered content */}
            {seasons.length === 0 ? (
              <p className="text-gray-400 text-lg">No hay temporadas disponibles.</p>
            ) : (
              <div 
                className="min-h-[500px] flex items-start justify-center pt-4 w-full"
                data-testid="seasons-container"
              >
                <div 
                  className="flex flex-wrap justify-center gap-4 md:gap-6"
                  style={{
                    maxWidth: `${getItemsPerRow(seasons.length) * 280 + (getItemsPerRow(seasons.length) - 1) * 24}px`
                  }}
                  data-testid="seasons-grid"
                >
                  {seasons.map((season, index) => (
                    <div 
                      key={season.id} 
                      className="w-[calc(50%-8px)] md:w-[280px]"
                    >
                      <SeasonCard
                        season={season}
                        onClick={handleSeasonSelect}
                        index={index}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>

        /* Level 1: Shows Grid View (home) */
        ) : (
          <>
            <section className="animate-fade-in mb-8" data-testid="shows-section">
              <div className="flex justify-center mb-10">
                <h2 
                  className="section-title w-full text-center px-8 py-3 bg-black text-white text-2xl md:text-3xl uppercase tracking-wide hand-drawn-subtle"
                  style={{ borderWidth: '3px', borderColor: '#fff', borderStyle: 'solid' }}
                >
                  Shows
                </h2>
              </div>
              
              {shows.length === 0 ? (
                <div className="text-center py-20 px-4" data-testid="empty-state">
                  <div className="text-6xl mb-4">😅</div>
                  <p className="text-white text-xl mb-2 font-bold">
                    {emptyPhrase}
                  </p>
                  <p className="text-gray-400 text-sm">
                    Pero no te awites, pronto habrá más contenido
                  </p>
                </div>
              ) : (
                <>
                  {/* Shows Grid - Limited to 4 on mobile */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6" data-testid="shows-grid">
                    {shows.map((show, index) => (
                      <div 
                        key={show.id} 
                        className={index >= 4 ? 'hidden sm:block' : ''}
                      >
                        <ShowCard
                          show={show}
                          onClick={handleShowSelect}
                          index={index}
                        />
                      </div>
                    ))}
                  </div>
                  
                  {/* "Más Shows" button - Only on mobile when more than 4 shows */}
                  {shows.length > 4 && (
                    <div className="flex justify-center mt-6 sm:hidden">
                      <button
                        onClick={() => {
                          // Remove the mobile limit by showing all
                          document.querySelectorAll('[data-testid="shows-grid"] > div').forEach(el => {
                            el.classList.remove('hidden');
                          });
                          // Hide this button
                          document.querySelector('[data-testid="more-shows-btn"]')?.classList.add('hidden');
                        }}
                        className="px-6 py-3 bg-[#E91E8C] text-white font-black uppercase tracking-wide hand-drawn-subtle hover:bg-[#E91E8C]/80 transition-colors"
                        style={{ borderWidth: '3px', borderColor: '#fff', borderStyle: 'solid' }}
                        data-testid="more-shows-btn"
                      >
                        Más Shows
                      </button>
                    </div>
                  )}
                </>
              )}
            </section>

            {/* Shopify Products Section */}
            <div className="pt-8">
              <ProductsSection 
                title="Tienda Oficial" 
                subtitle="Merch de Darkar Company"
                limit={4}
                showViewAll={true}
                compact={true}
              />
            </div>

            {/* Music Section */}
            <div className="pt-16">
              <MusicSection />
            </div>
          </>
        )}
        </div>
      </main>
      
      <div className="mt-auto">
        <Footer />
      </div>
    </div>
  );
}
